// ═══ Agora Audio Player ═══
//
// wavesurfer.js-backed audio player for Agora posts. Designed to live inside
// a PostCard component — renders a waveform, transport controls, cue buttons,
// and exposes an imperative seek() method for timestamp-link click handlers
// in the post body.
//
// Collaboration shape (2026-04-16):
// - Timbre owns this component (music/audio intelligence)
// - Griot owns the Agora migration + PostCard shape
// - Handoff: PostCard mounts <AgoraAudioPlayer /> and forwards a ref for seek()
//
// Dependencies: wavesurfer.js (already in gathering-v3's package.json)

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'

export interface AudioCue {
  label: string
  time_seconds: number
  end_seconds?: number | null
}

export interface AgoraAudioPlayerRef {
  /** Seek to a position. If endSeconds is supplied, loops once between [start, end]. */
  seek: (startSeconds: number, endSeconds?: number | null) => void
  /** Current playback position in seconds (or 0 if not ready). */
  getCurrentTime: () => number
  /** Total audio duration in seconds (or 0 if not ready). */
  getDuration: () => number
}

interface Props {
  audioUrl: string
  cues?: AudioCue[]
  /** Optional color overrides; defaults match Agora purple accent. */
  waveColor?: string
  progressColor?: string
  cursorColor?: string
  height?: number
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

const AgoraAudioPlayer = forwardRef<AgoraAudioPlayerRef, Props>(function AgoraAudioPlayer(
  {
    audioUrl,
    cues,
    waveColor = '#4a4458',
    progressColor = '#856FC8',
    cursorColor = '#B294DD',
    height = 48,
  },
  ref,
) {
  const waveformRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const loopEndRef = useRef<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [activeCueIdx, setActiveCueIdx] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!waveformRef.current || !audioUrl) return

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor,
      progressColor,
      cursorColor,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height,
      normalize: true,
      url: audioUrl,
    })

    wavesurferRef.current = ws

    ws.on('ready', () => setDuration(ws.getDuration()))
    ws.on('play', () => setIsPlaying(true))
    ws.on('pause', () => setIsPlaying(false))
    ws.on('finish', () => setIsPlaying(false))
    ws.on('audioprocess', () => {
      const t = ws.getCurrentTime()
      setCurrentTime(t)
      // Loop enforcement: if a loop end is set and we've passed it, stop.
      if (loopEndRef.current != null && t >= loopEndRef.current) {
        ws.pause()
        loopEndRef.current = null
        setActiveCueIdx(null)
      }
    })
    ws.on('error', (err) => {
      setError(String((err as Error)?.message || err))
    })

    return () => {
      ws.destroy()
      wavesurferRef.current = null
      loopEndRef.current = null
    }
  }, [audioUrl, waveColor, progressColor, cursorColor, height])

  useImperativeHandle(
    ref,
    () => ({
      seek: (startSeconds, endSeconds) => {
        const ws = wavesurferRef.current
        if (!ws) return
        const dur = ws.getDuration() || 1
        ws.seekTo(Math.min(startSeconds, dur) / dur)
        loopEndRef.current = typeof endSeconds === 'number' ? endSeconds : null
        ws.play()
      },
      getCurrentTime: () => wavesurferRef.current?.getCurrentTime() ?? 0,
      getDuration: () => wavesurferRef.current?.getDuration() ?? 0,
    }),
    [],
  )

  const togglePlay = () => {
    const ws = wavesurferRef.current
    if (!ws) return
    loopEndRef.current = null
    setActiveCueIdx(null)
    ws.playPause()
  }

  const playCue = (cue: AudioCue, idx: number) => {
    const ws = wavesurferRef.current
    if (!ws) return
    const dur = ws.getDuration() || 1
    ws.seekTo(Math.min(cue.time_seconds, dur) / dur)
    loopEndRef.current = typeof cue.end_seconds === 'number' ? cue.end_seconds : null
    setActiveCueIdx(idx)
    ws.play()
  }

  if (error) {
    return (
      <div style={styles.error}>Couldn't load audio: {error}</div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.top}>
        <button
          type="button"
          onClick={togglePlay}
          style={styles.playBtn}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '❚❚' : '▶'}
        </button>
        <div ref={waveformRef} style={styles.waveform} />
        <div style={styles.time}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
      {cues && cues.length > 0 && (
        <div style={styles.cues}>
          {cues.map((cue, idx) => (
            <button
              key={`${cue.label}-${idx}`}
              type="button"
              onClick={() => playCue(cue, idx)}
              style={{
                ...styles.cueBtn,
                ...(activeCueIdx === idx ? styles.cueBtnActive : {}),
              }}
            >
              {cue.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

const styles: Record<string, React.CSSProperties> = {
  container: {
    margin: '4px 0 14px',
    padding: '14px 16px',
    background: 'var(--bg-elevated, #191921)',
    border: '1px solid var(--border, #2a2a35)',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  playBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#856FC8',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  waveform: {
    flex: 1,
    minWidth: 0,
    height: 48,
    cursor: 'pointer',
  },
  time: {
    fontSize: 11,
    color: 'var(--text-dim, #888)',
    fontVariantNumeric: 'tabular-nums',
    minWidth: 82,
    textAlign: 'right',
  },
  cues: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  cueBtn: {
    fontSize: 11,
    padding: '3px 8px',
    borderRadius: 12,
    background: 'var(--bg-surface, #121218)',
    border: '1px solid var(--border, #2a2a35)',
    color: 'var(--text-secondary, #bbb)',
    cursor: 'pointer',
    transition: 'border-color 0.15s, color 0.15s',
  },
  cueBtnActive: {
    borderColor: '#856FC8',
    color: '#856FC8',
  },
  error: {
    margin: '4px 0 14px',
    padding: '10px 14px',
    background: 'rgba(200, 60, 60, 0.08)',
    border: '1px solid rgba(200, 60, 60, 0.3)',
    borderRadius: 8,
    color: '#d88',
    fontSize: 12,
  },
}

export default AgoraAudioPlayer
